// useFormApi.ts
import * as React from "react"
import type { FormApi, FormSpec } from "./formSpec"
import { getFieldError } from "./formValidation"

type Params = {
    spec: FormSpec
    values: Record<string, unknown>
    setValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>
    errors: Record<string, { message: string }>
    setErrors: React.Dispatch<React.SetStateAction<Record<string, { message: string }>>>
    shouldValidate: (event: "onChange" | "onBlur" | "onSubmit") => boolean
    validateField: (name: string) => Promise<0 | 1>
    validateFieldWithValues: (name: string, nextValues: Record<string, unknown>) => Promise<0 | 1>
}

export function useFormApi({
    spec,
    values,
    setValues,
    errors,
    setErrors,
    shouldValidate,
    validateField,
    validateFieldWithValues,
}: Params): FormApi {
    return React.useMemo(() => {
        return {
            register: (name: string) => {
                return {
                    name,
                    value: values[name],
                    onChange: async (e: any) => {
                        const next =
                            e && typeof e === "object" && "target" in e
                                ? (e.target as any).type === "checkbox"
                                    ? Boolean((e.target as any).checked)
                                    : (e.target as any).value
                                : e

                        const nextValues = { ...values, [name]: next }
                        setValues(nextValues)

                        if (!shouldValidate("onChange")) return

                        const def = (spec.fields as any)?.[name]
                        if (!def) return

                        const msg = await getFieldError(def, nextValues)
                        if (!msg) {
                            setErrors((prev) => {
                                if (!prev[name]) return prev
                                const copy = { ...prev }
                                delete copy[name]
                                return copy
                            })
                            return
                        }
                        setErrors((prev) => ({ ...prev, [name]: { message: msg } }))
                    },
                    onBlur: () => {
                        if (!shouldValidate("onBlur")) return
                        void validateField(name)
                    },
                }
            },

            getValue: (name: string) => values[name],

            setValue: (name: string, value: unknown) => {
                const nextValues = { ...values, [name]: value }
                setValues(nextValues)

                if (!shouldValidate("onChange")) return
                void validateFieldWithValues(name, nextValues)
            },

            setError: (name: string, message: string) => setErrors((prev) => ({ ...prev, [name]: { message } })),

            clearErrors: (name?: string) => {
                if (!name) {
                    setErrors({})
                    return
                }
                setErrors((prev) => {
                    if (!prev[name]) return prev
                    const copy = { ...prev }
                    delete copy[name]
                    return copy
                })
            },

            validateField,

            formState: { errors },
        }
    }, [errors, setErrors, setValues, shouldValidate, spec.fields, validateField, validateFieldWithValues, values])
}
